import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Heading, Text, Button, Radio, TextArea } from '@indeed/ifl-components';
import { GettextInterface } from '@indeed/indeed-gettext';
import PageTransition from '../../../components/PageTransition/PageTransition';
import JobDescription from '../../../components/JobDescription/JobDescription';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';

export interface QuestionsPageProps {
    step?: string;
    gt?: GettextInterface;
}

interface Question {
    id: string;
    question: string;
    type: 'radio' | 'textarea';
    options?: string[];
    required: boolean;
}

const questions: Question[] = [
    {
        id: 'availability',
        question: 'What is your availability to start this position?',
        type: 'radio',
        options: ['Immediately', 'Within 2 weeks', 'Within 1 month', 'More than 1 month', 'Other'],
        required: true,
    },
    {
        id: 'remote_preference',
        question: 'What is your preference for remote work?',
        type: 'radio',
        options: [
            'Fully remote',
            'Hybrid (some remote, some in-office)',
            'Fully in-office',
            'No preference',
        ],
        required: true,
    },
    {
        id: 'salary_expectations',
        question: 'What are your salary expectations for this role?',
        type: 'radio',
        options: [
            '$60,000 - $80,000',
            '$80,000 - $100,000',
            '$100,000 - $120,000',
            '$120,000 - $150,000',
            'Above $150,000',
            'Prefer to discuss',
        ],
        required: true,
    },
    {
        id: 'motivation',
        question:
            'Why are you interested in this position? What motivates you about this opportunity?',
        type: 'textarea',
        required: true,
    },
    {
        id: 'additional_info',
        question:
            'Is there any additional information you would like to share about your candidacy?',
        type: 'textarea',
        required: false,
    },
];

const QuestionsPage: React.FunctionComponent<QuestionsPageProps> = () => {
    const { goBack, canGoBack, goForward } = usePageFlow();
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleRadioChange = (questionId: string, value: string): void => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
        if (errors[questionId]) {
            setErrors((prev) => ({ ...prev, [questionId]: '' }));
        }
    };

    const handleTextareaChange = (questionId: string, value: string): void => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
        if (errors[questionId]) {
            setErrors((prev) => ({ ...prev, [questionId]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        questions.forEach((question) => {
            if (question.required && !answers[question.id]?.trim()) {
                newErrors[question.id] = 'This field is required';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleBack = (): void => {
        void goBack();
    };

    const handleContinue = (): void => {
        if (validateForm()) {
            void goForward();
        }
    };

    const handleSkip = (): void => {
        void goForward();
    };

    return (
        <PageTransition pageKey="questions">
            <Helmet>
                <title>Application Questions - Indeed</title>
                <meta
                    name="description"
                    content="Answer additional questions about your job application and preferences."
                />
                <meta
                    name="keywords"
                    content="job application questions, interview questions, job preferences, Indeed"
                />
            </Helmet>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: ['1fr', '1fr 1fr'],
                    gap: 6,
                    alignItems: 'start',
                }}
            >
                {/* Left Section - Questions */}
                <Box
                    sx={{
                        backgroundColor: 'background.default',
                        padding: 6,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'border.subtle',
                    }}
                >
                    <Heading
                        as="h2"
                        level={2}
                        sx={{
                            color: 'foreground.primary',
                            marginBlockEnd: 2,
                        }}
                    >
                        Application Questions
                    </Heading>
                    <Text sx={{ color: 'foreground.secondary', fontSize: 2, marginBlockEnd: 6 }}>
                        Please answer the following questions to help us better understand your
                        application.
                    </Text>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {questions.map((question) => (
                            <Box key={question.id}>
                                <Text
                                    sx={{
                                        color: 'foreground.primary',
                                        fontWeight: 'semibold',
                                        marginBlockEnd: 3,
                                        display: 'block',
                                    }}
                                >
                                    {question.question}
                                    {question.required && (
                                        <Text
                                            as="span"
                                            sx={{ color: 'danger.default', marginInlineStart: 1 }}
                                        >
                                            *
                                        </Text>
                                    )}
                                </Text>

                                {question.type === 'radio' && question.options && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {question.options.map((option) => (
                                            <Radio
                                                key={option}
                                                name={question.id}
                                                value={option}
                                                checked={answers[question.id] === option}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                ) => handleRadioChange(question.id, e.target.value)}
                                                sx={{ alignItems: 'flex-start' }}
                                            >
                                                {option}
                                            </Radio>
                                        ))}
                                    </Box>
                                )}

                                {question.type === 'textarea' && (
                                    <TextArea
                                        value={answers[question.id] || ''}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                            handleTextareaChange(question.id, e.target.value)
                                        }
                                        placeholder="Enter your response..."
                                        rows={4}
                                        sx={{ inlineSize: '100%' }}
                                    />
                                )}

                                {errors[question.id] && (
                                    <Text
                                        sx={{
                                            color: 'danger.default',
                                            fontSize: 1,
                                            marginBlockStart: 2,
                                        }}
                                    >
                                        {errors[question.id]}
                                    </Text>
                                )}
                            </Box>
                        ))}
                    </Box>

                    {/* Navigation Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 3,
                            marginBlockStart: 8,
                        }}
                    >
                        <Box>
                            {canGoBack && (
                                <Button variant="secondary" onClick={handleBack}>
                                    Back
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                            <Button variant="tertiary" onClick={handleSkip}>
                                Skip for Now
                            </Button>
                            <Button variant="primary" onClick={handleContinue}>
                                Continue
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Right Section - Job Description */}
                <JobDescription />
            </Box>
        </PageTransition>
    );
};

export default QuestionsPage;
